import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, ButtonBase, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import useModuleAccess from "../../hooks/useModuleAccess";
import ModuleIconBadge from "../ModuleIconBadge";
import { canAccessNavItem, userNavItems } from "../MainNavs/navItems";
import { isNavHrefActive } from "../MainNavs/navLinkActive";

/**
 * The app's top-level destinations (Project, AIMS, Software, Client data,
 * Licences) as module badges: a strip of labelled tiles while the sidebar is
 * expanded, a column of icons with tooltips on the collapsed rail. Only the
 * items the user's licence opens are listed.
 */
export default function SidebarAppLinks({ expanded }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user, allowedModules } = useModuleAccess();

  const visibleItems = userNavItems.filter(
    (item) =>
      item.type === "link" && canAccessNavItem(item, { user, allowedModules }),
  );
  if (visibleItems.length === 0) return null;

  return (
    <Box
      component="nav"
      aria-label="Applications"
      sx={{
        display: "flex",
        flexDirection: expanded ? "row" : "column",
        alignItems: "center",
        justifyContent: expanded ? "space-between" : "flex-start",
        gap: expanded ? 0.5 : 1,
        px: expanded ? 1.5 : 0,
        py: 1.25,
      }}
    >
      {visibleItems.map((item) => {
        const isActive = isNavHrefActive(pathname, item.link);

        return (
          <Tooltip
            key={item.label}
            title={expanded ? "" : item.label}
            placement="right"
          >
            <ButtonBase
              onClick={() => navigate(item.link)}
              aria-current={isActive ? "page" : undefined}
              sx={{
                flex: expanded ? 1 : "none",
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.5,
                px: 0.5,
                py: 0.75,
                borderRadius: 2,
                color: isActive ? "text.primary" : "text.secondary",
                bgcolor: (theme) =>
                  isActive
                    ? alpha(
                        theme.palette.primary.main,
                        theme.palette.mode === "dark" ? 0.18 : 0.08,
                      )
                    : "transparent",
                "&:hover": {
                  bgcolor: "action.hover",
                  color: "text.primary",
                },
              }}
            >
              <ModuleIconBadge
                moduleKey={item.visualKey}
                size={expanded ? "md" : "sm"}
              />
              {expanded && (
                <Typography
                  variant="body2"
                  noWrap
                  sx={{
                    fontSize: "0.7rem",
                    fontWeight: isActive ? 700 : 600,
                    maxWidth: "100%",
                  }}
                >
                  {item.label}
                </Typography>
              )}
            </ButtonBase>
          </Tooltip>
        );
      })}
    </Box>
  );
}
